import * as React from "react";
import { theme } from "@/theme/theme";
import { cn } from "@/lib/utils";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "ghost";
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "primary",
      disabled = false,
      onClick,
      className,
      ...props
    },
    ref,
  ) => {
    const baseStyles: React.CSSProperties = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: theme.radius.md,
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.sizes.body,
      fontWeight: theme.typography.weights.medium,
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      border: "none",
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "all 0.2s ease",
    };

    const variantStyles: React.CSSProperties = (() => {
      if (disabled) {
        return {
          backgroundColor: theme.colors.gray[300],
          color: theme.colors.gray[500],
        };
      }

      switch (variant) {
        case "primary":
          return {
            backgroundColor: theme.colors.primary.blue,
            color: theme.colors.white,
          };
        case "secondary":
          return {
            backgroundColor: theme.colors.gray[100],
            color: theme.colors.gray[900],
            border: `1px solid ${theme.colors.gray[300]}`,
          };
        case "ghost":
          return {
            backgroundColor: "transparent",
            color: theme.colors.gray[700],
          };
        default:
          return {
            backgroundColor: theme.colors.primary.blue,
            color: theme.colors.white,
          };
      }
    })();

    const hoverStyles: React.CSSProperties = (() => {
      if (disabled) return {};

      switch (variant) {
        case "primary":
          return {
            backgroundColor: "#1976D2", // darker blue
          };
        case "secondary":
          return {
            backgroundColor: theme.colors.gray[100],
            boxShadow: theme.shadows.card,
          };
        case "ghost":
          return {
            backgroundColor: theme.colors.gray[100],
          };
        default:
          return {};
      }
    })();

    return (
      <button
        ref={ref}
        disabled={disabled}
        onClick={onClick}
        className={cn(className)}
        style={{ ...baseStyles, ...variantStyles }}
        onMouseEnter={(e) => {
          Object.assign(e.currentTarget.style, hoverStyles);
        }}
        onMouseLeave={(e) => {
          Object.assign(e.currentTarget.style, variantStyles);
        }}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

// Legacy support for buttonVariants (for backward compatibility with existing components)
import { cva } from "class-variance-authority";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);
